import type { FollowUpSummary } from "shared/types/candidate-follow-up";
import { FunctionComponent, SVGProps } from "react";
import {
  getStatusLabel,
  type CandidacyStatus,
} from "screens/candidacies/candidacyPhaseConfig";
import {
  Checkmark,
  Decline,
  Incoming,
  Pulse,
  Rocket,
  Process,
  Notifications,
} from "assets/icons";

// ========== INTERFACES ==========

export interface CandidateResident {
  id: string;
  firstName?: string;
  lastName?: string;
  incomeMonth1?: string | null;
  incomeMonth2?: string | null;
  incomeMonth3?: string | null;
  idFront?: string | null;
  idBack?: string | null;
}

export interface CandidateInfo {
  status: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
    phoneNumber?: string;
  };
  createDate: Date;
  visitDate: Date;
  invitationSentAt?: Date | null;
  reminderSentAt?: Date | null;
  id: string;

  numberOfAdults: number;
  numberOfChildren: number;
  hasPets: boolean;
  smoking: boolean;

  checklist: {
    label: string;
    isChecked: boolean;
    explanation: string | null;
  }[];

  unitId: string;
  buildingId: string;

  notInterested: {
    reason: string;
    details: string;
  } | null;

  residents?: CandidateResident[];
  alternateDates?: (Date | string)[];
  appointmentType?: string;

  source?: string;
  // True when this row comes from the Invited table, false for an Application.
  // Set by the backend reformatters. This is the source of truth for archive/email
  // routing — do NOT infer the entity type from `status`, since derived statuses
  // like "reminder-sent" and "forwarded-to-broker" exist in both tables.
  isInvited?: boolean;
  followUpStatus?: "none" | "ok" | "problem";
  followUpRemark?: string | null;
  followUpSummary?: FollowUpSummary | null;
  emailSchedules?: CandidateEmailScheduleEntry[];
}

export type EmailScheduleType =
  | "post_visit_day1"
  | "post_visit_day2"
  | "post_visit_day3"
  | "interested_follow_up";

export type EmailScheduleStatus = "not_sent" | "sent" | "failed";

export interface CandidateEmailScheduleEntry {
  id: number;
  applicationId: number;
  emailType: EmailScheduleType;
  scheduledDate: string;
  status: EmailScheduleStatus;
  sentAt: string | null;
  errorMessage: string | null;
}

export interface TimeslotDetail {
  id: number;
  startTime: string;
  endTime: string;
  bookingCount: number;
  maxCapacity: number;
}

export interface BrokerInfo {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone_mobile?: string;
  company?: { id?: number; name?: string } | null;
}

export interface UnitInfoEntry {
  status: "vacant" | "upcoming" | "renovation" | "concept" | "rented";
  availableTimeslots: number;
  timeslotDetails: TimeslotDetail[];
  visitationContact: { firstName?: string; lastName?: string; picture?: string | null } | null;
  brokerContacts: BrokerInfo[];
  buildingAddress: string | null;
}

export interface Filters {
  unit: string;
  building: string;
  status: string;
  statusArray?: string[];
  searchText?: string;
  /** Verberg kandidaten waarvan de unit op "Verhuurd" staat. Standaard uit. */
  excludeRentedUnits?: boolean;
}

export interface CandidateTableProps {
  buildingId?: string;
  handleOpenAddContractModal: (
    contacts?: Array<{ id: number; name: string; isOnContract: boolean; resourceEntityId?: number; wasExisting?: boolean; residentData?: { email?: string | null; phoneNumber?: string | null; spokenLanguages?: string[]; nationality?: string | null; dateOfBirth?: string | null; placeOfBirth?: string | null; relationToApplicant?: string | null } }>,
    unitId?: string,
    contractType?: any,
    totalResidents?: number,
  ) => void;
  filters?: Filters;
  includeArchived?: boolean;
  onCandidateClick?: (candidate: CandidateInfo) => void;
}

export interface ResidentDocumentAsset {
  fileType: string;
  image: string;
}

// ========== HELPER FUNCTIONS ==========

export const statusTranslation = (
  status: string,
): [
  string,
  FunctionComponent<SVGProps<SVGSVGElement>> & { title?: string },
  string,
  string,
] => {
  const label = getStatusLabel(status as CandidacyStatus);

  switch (status) {
    case "approved":
      return [label, Checkmark, "text-green-700", "border-green-500"];
    case "tenant":
      return [label, Pulse, "text-green-700", "border-green-500"];
    case "rejected":
      return [label, Decline, "text-red-700", "border-red-500"];
    case "not-interested":
      return [label, Decline, "text-red-700", "border-red-500"];
    case "awaiting-visit":
      return [label, Incoming, "text-blue-700", "border-blue-500"];
    case "awaiting-interested":
      return [label, Process, "text-blue-700", "border-blue-500"];
    case "awaiting-alternate-selection":
      return [label, Process, "text-blue-700", "border-blue-500"];
    case "invited":
      return [label, Incoming, "text-blue-700", "border-blue-500"];
    case "reminder-sent":
      return [label, Incoming, "text-violet-700", "border-violet-500"];
    case "forwarded-to-broker":
      return [label, Rocket, "text-amber-700", "border-amber-500"];
    case "interested":
      return [label, Rocket, "text-orange-700", "border-orange-500"];
    case "notify":
      return [label, Notifications, "text-orange-700", "border-orange-500"];
    case "searchingAppointmentMoment":
      return [label, Process, "text-red-700", "border-red-500"];
    default:
      return ["", Decline, "", ""];
  }
};

export const labelTranslator = (label: string) => {
  switch (label) {
    case "Financials":
      return { label: "Betalingsverbintenis", swapStatus: false };
    case "Income":
      return { label: "Netto inkomensgarantie", swapStatus: false };
    case "Documents":
      return { label: "Doorsturen van documenten", swapStatus: false };
    case "mainResidence":
      return {
        label: "Hoofdverblijfplaats",
        swapStatus: false,
        onBad: "Niet de",
      };
    case "awareNoLift":
      return {
        label: "Op de hoogte van 'geen de lift'",
        swapStatus: false,
        onBad: "Niet",
      };
    case "petsInResidence":
      return { label: "Huisdieren", swapStatus: true, onGood: "Geen" };
    case "smokingInResidence":
      return { label: "Roker", swapStatus: true, onGood: "Geen" };
    default:
      return { label: label, swapStatus: false };
  }
};

/**
 * Transform raw API candidate data into typed CandidateInfo with proper Date objects.
 */
export const transformCandidateResponse = (data: any[]): CandidateInfo[] =>
  data.map((candidate: any) => ({
    ...candidate,
    createDate: new Date(candidate.createDate),
    visitDate: candidate.visitDate ? new Date(candidate.visitDate) : null,
    invitationSentAt: candidate.invitationSentAt ? new Date(candidate.invitationSentAt) : null,
    reminderSentAt: candidate.reminderSentAt ? new Date(candidate.reminderSentAt) : null,
    moveDate: {
      agreement: candidate.moveDate?.agreement ?? false,
      explanation: candidate.moveDate?.explanation
        ? new Date(candidate.moveDate.explanation)
        : null,
    },
    residents:
      candidate.residents?.map((resident: any) => ({
        ...resident,
        dateOfBirth: new Date(resident.dateOfBirth),
      })) || [],
  }));
